/* Endpoints de autenticação — magic link */
import { fetchWithAuth } from './client';
import type { RequestCodeResponse, VerifyCodeResponse, Locale } from '../types';

/**
 * Solicita código de 6 dígitos por e-mail.
 * Backend: POST /api/auth/request-code
 * Rate limit: 5 requisições/hora por e-mail.
 */
export async function requestCode(
  email: string,
  locale: Locale = 'pt-BR',
  origin: string = 'web-trial',
): Promise<RequestCodeResponse> {
  const response = await fetchWithAuth('/api/auth/request-code', {
    method: 'POST',
    auth: false, // não precisa de JWT para solicitar código
    body: JSON.stringify({ email, locale, origin }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? 'Erro ao enviar código');
  }

  return response.json();
}

/**
 * Verifica código de 6 dígitos e retorna JWT + dados do usuário.
 * Backend: POST /api/auth/verify-code
 *
 * Adaptador: backend retorna { ok, token, userId, email, tier }
 * Web app espera VerifyCodeResponse { jwt, user: { id, email, tier, ... } }
 *
 * Trial v2 (TRIAL_V2_API.md §2): `anonId` opcional migra o trial anônimo
 * (relógio + casos + memória de conversa) pro UserRecord. Backend ignora o
 * campo com flag off, e pagantes/whitelist NUNCA recebem trial_v2.
 */
export async function verifyCode(
  email: string,
  code: string,
  anonId?: string,
): Promise<VerifyCodeResponse> {
  const response = await fetchWithAuth('/api/auth/verify-code', {
    method: 'POST',
    auth: false, // ainda sem JWT ao verificar
    body: JSON.stringify(
      anonId ? { email, code, anonId } : { email, code },
    ),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? 'Código inválido');
  }

  const raw = (await response.json()) as {
    ok: boolean;
    token: string;
    userId: string;
    email: string;
    tier: 'free' | 'plantonista' | 'especialista' | 'unlimited';
  };

  if (!raw.token || typeof raw.token !== 'string') {
    throw new Error('JWT inválido retornado pelo servidor');
  }

  return {
    jwt: raw.token,
    user: {
      id: raw.userId,
      email: raw.email,
      tier: raw.tier,
    },
  } as VerifyCodeResponse;
}
